import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReviewSelectInput, ReviewWizardRatingInput, ReviewWizardTextInput } from 'trellis-ecosystem';
import { PENDING } from '../../../constants/UnreviewableReason';
import { useGetCatalogServices } from '../../../hooks/useGetCatalogServices';
import { EcosystemReviewWizard } from '../EcosystemReviewWizard';
import { SolutionsPartnerProfileReviewWizardHeader } from './SolutionsPartnerProfileReviewWizardHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VALIDATION_SCHEMA = {
  content: {
    TITLE: {
      required: true
    },
    REVIEW: {
      required: true,
      minCharacters: 75,
      maxCharacters: 3000
    }
  },
  ratings: {
    OVERALL: {
      required: true
    }
  },
  tags: {
    CATALOG_SERVICES: {
      required: true,
      minSelected: 1,
      maxSelected: 10
    }
  }
};
export function SolutionsPartnerProfileReviewWizard({
  editedReview,
  iconUrl,
  isPublic,
  name,
  onClose,
  onSuccess,
  onTrackInteraction,
  profileId,
  unreviewableReason
}) {
  const {
    catalogServices
  } = useGetCatalogServices({
    skip: false
  });
  const isEditing = !!editedReview;
  const emptyReview = {
    content: {},
    ratings: {
      OVERALL: 0
    },
    tags: {
      CATALOG_SERVICES: []
    }
  };
  let defaultReview;
  try {
    const storedReview = JSON.parse(sessionStorage.getItem(`PROFILE_${profileId}_review`));
    defaultReview = storedReview || emptyReview;
  } catch (__error) {
    defaultReview = emptyReview;
  }
  const [overallRating, setOverallRating] = useState(defaultReview.ratings['OVERALL'] || undefined);
  const [completedAnswerFields, setCompletedAnswerFields] = useState(Object.entries(defaultReview.content).filter(([field, value]) => field && !!value).map(([field]) => field));
  useEffect(() => {
    onTrackInteraction({
      action: isEditing ? 'open edit review wizard' : 'open review wizard',
      section: 'review'
    });
  }, [isEditing, onTrackInteraction]);
  useEffect(() => {
    if (!isEditing && unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.error.ReviewError.GENERIC_UNREVIEWABLE"
        }),
        type: 'danger'
      });
    }
  }, [isEditing, unreviewableReason]);
  const handleSuccess = useCallback(() => {
    onTrackInteraction({
      action: 'submit review',
      section: 'review'
    });
    sessionStorage.removeItem(`PROFILE_${profileId}_review`);
    onSuccess();
  }, [onSuccess, profileId, onTrackInteraction]);
  const handleClose = useCallback(() => {
    onTrackInteraction({
      action: 'dismiss review wizard',
      section: 'review'
    });
    onClose();
  }, [onClose, onTrackInteraction]);
  const onChange = useCallback(review => {
    if (review.ratings['OVERALL'] !== overallRating) {
      onTrackInteraction({
        action: 'set overall rating',
        section: 'review',
        value: review.ratings['OVERALL']
      });
      setOverallRating(review.ratings['OVERALL']);
    }
    const answerFields = Object.entries(review.content).filter(([field, value]) => field && !!value).map(([field]) => field);
    answerFields.forEach(field => {
      if (!completedAnswerFields.includes(field)) {
        onTrackInteraction({
          action: 'add review answer',
          section: 'review',
          value: field
        });
      }
    });
    setCompletedAnswerFields(answerFields);
    if (!isEditing) {
      sessionStorage.setItem(`PROFILE_${profileId}_review`, JSON.stringify(review));
    }
  }, [completedAnswerFields, isEditing, profileId, onTrackInteraction, overallRating]);
  const WizardHeader = useMemo(() => () => /*#__PURE__*/_jsx(SolutionsPartnerProfileReviewWizardHeader, {
    iconUrl: iconUrl,
    name: name,
    isEditing: isEditing
  }), [iconUrl, name, isEditing]);
  if (isPublic || !isEditing && unreviewableReason) {
    return null;
  }
  const serviceOptions = (catalogServices || []).map(service => ({
    value: service.id,
    text: service.name
  }));
  return /*#__PURE__*/_jsxs(EcosystemReviewWizard, {
    defaultReview: defaultReview,
    editedReview: editedReview,
    HeaderComponent: WizardHeader,
    offeringId: profileId,
    onChange: onChange,
    onClose: handleClose,
    onSuccess: handleSuccess,
    ratingSection: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        as: "span",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h5",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.wizard.profile.header",
            options: {
              name
            }
          })
        })
      }), /*#__PURE__*/_jsx(ReviewWizardRatingInput, {
        fieldKey: "OVERALL",
        label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.OVERALL')
      })]
    }),
    reviewType: "PROFILE",
    requireEditReason: true,
    validationSchema: VALIDATION_SCHEMA,
    children: [/*#__PURE__*/_jsx(ReviewSelectInput, {
      fieldKey: "CATALOG_SERVICES",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.CATALOG_SERVICES'),
      multi: true,
      options: serviceOptions,
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.CATALOG_SERVICES')
    }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
      fieldKey: "TITLE",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.TITLE'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.TITLE'),
      resize: false,
      rows: 1
    }), /*#__PURE__*/_jsx(ReviewWizardTextInput, {
      fieldKey: "REVIEW",
      label: unescapedText('marketplace-ui-reviews.wizard.profile.labels.REVIEW'),
      placeholder: unescapedText('marketplace-ui-reviews.wizard.profile.placeholders.REVIEW', {
        name
      }),
      rows: 4
    })]
  });
}